import { Auth0Client } from '@auth0/auth0-spa-js';
import { LicenseInfo } from '@mui/x-license';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter as _createRouter, RouterProvider } from '@tanstack/react-router';
import { createTRPCClient, isTRPCClientError } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import invariant from 'tiny-invariant';

import { routeTree } from '#generated/route-tree.js';
import { doRedirects, getTanstackQueryOptions, tanstackRouterOptions } from './entry-client-utils.js';
import { reportChunkLoadError } from './screens/error-boundaries/chunk-load-recovery.js';
import { StaleDeployPrompt } from './screens/error-boundaries/stale-deploy-prompt.js';
import { Auth0Context } from './state/contexts/auth0-context.js';
import { StyleProviders } from './style-providers.js';
import { TRPCProvider } from './trpc.js';
import {
	captureAnalyticsEvent,
	setAnalyticsRouteContextSource,
	withAnalyticsRouteContext,
} from './utils/analytics/analytics-events.js';
import { getRouteAnalyticsContext } from './utils/analytics/route-analytics-context.js';
import { registerPageviewTracking } from './utils/analytics/track-pageviews.js';
import { isAuth0CallbackPathname } from './utils/auth0-callback-route.js';
import { auth0UserQueryOptions } from './utils/auth0-user-query-options.js';
import { extractBackendHttpError } from './utils/backend-http-error.js';
import {
	clearPersistentQueryCachesFromSearchParams,
	runPersistentQueryCacheGc,
} from './utils/persistent-query-cache/persistent-query-cache.js';
import { createTrpcLinks } from './utils/trpc-links.js';

import type { AppRouter } from '@cadstrom/backend/trpc';
import type { MongoAbility } from '@casl/ability';
import type { TRPCClient } from '@trpc/client';
import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { PostHog } from 'posthog-js';
import type { PageNavigationProviderRouteConfig } from '#components/command-bar/providers/page-navigation-provider.js';
import type { Auth0ContextValue } from './state/contexts/auth0-context.js';

doRedirects();

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
	api_host: '/ati-3b9c3', // Random prefix to throw off adblockers
	ui_host: 'https://us.posthog.com',
	defaults: '2025-05-24',
	person_profiles: 'always',
	// Pageviews are captured manually per navigation (see registerPageviewTracking)
	// so they carry the low-cardinality route id instead of raw UUID URLs.
	capture_pageview: false,
	loaded: (posthog) => {
		// Disable PostHog in dev to avoid polluting the analytics data with test events.
		if (import.meta.env.DEV) {
			posthog.opt_out_capturing();
			posthog.set_config({ disable_session_recording: true });
		}
	},
	before_send: [withAnalyticsRouteContext],
});

// Expose the deployment environment (Vite mode, baked from BUILD_ENV at image
// build) to feature-flag evaluation so a flag can target one environment (e.g.
// sandbox or development) without affecting the other environments that share
// this PostHog project. Sent only with the flags request — deliberately not a
// durable person property, which the same person would overwrite when using
// another environment.
posthog.setPersonPropertiesForFlags({ environment: import.meta.env.MODE });

export interface RouterContext {
	auth0Client: Auth0Client;
	queryClient: QueryClient;
	trpc: TRPCOptionsProxy<AppRouter>;
	trpcClient: TRPCClient<AppRouter>;
	posthog: PostHog;
}

// https://tanstack.com/router/latest/docs/framework/react/guide/creating-a-router#router-type-safety
declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof createRouter>;
	}

	interface StaticDataRouteOption {
		/** CASL ability check — when present, the route is hidden from users who fail this check. */
		authorize?: (ability: MongoAbility) => boolean;
		navigation?: PageNavigationProviderRouteConfig;
	}
}

interface TanstackQueryMeta extends Record<string, unknown> {
	auth0Client: Auth0Client;
}

// https://tanstack.com/query/latest/docs/framework/react/typescript#registering-global-meta
declare module '@tanstack/react-query' {
	interface Register {
		queryMeta: TanstackQueryMeta;
		mutationMeta: TanstackQueryMeta;
	}
}

// Create router with a wrapper function as we need a static reference to the created router in order to register its
// with the module augmentation below, but we also need to pass in the context which is not available at module load
// time.
function createRouter(context: RouterContext) {
	return _createRouter({
		...tanstackRouterOptions,
		context,
		routeTree,
	});
}

async function clientInit() {
	const rootElement = document.getElementById('root');
	invariant(rootElement, 'Root element not found');

	const auth0Client = new Auth0Client({
		domain: import.meta.env.VITE_AUTH0_DOMAIN,
		clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
		// Use refresh tokens (stored in localStorage) for silent auth instead of the
		// iframe-based default, which depends on third-party cookies on the Auth0
		// tenant domain. Incognito sessions, Safari ITP, and Chrome's third-party
		// cookie phase-out all block those cookies and break silent auth otherwise.
		useRefreshTokens: true,
		cacheLocation: 'localstorage',
		authorizationParams: {
			redirect_uri: `${window.location.origin}/callback`,
			audience: import.meta.env.VITE_AUTH0_AUDIENCE,
		},
	});

	// Track errors that have been logged to avoid duplicates between query cache and error boundary
	const loggedErrors = new WeakSet<Error>();

	const tanstackQueryOptions = getTanstackQueryOptions(auth0Client);
	const queryClient = new QueryClient({
		...tanstackQueryOptions,
		queryCache: new QueryCache({
			onSuccess: (data, query) => {
				// Some of our API routes return an object with `isSuccess === false` if the request was not successful. We need
				// to log these to PostHog separately as they don't technically trigger exceptions.
				if (typeof data === 'object' && data !== null && 'isSuccess' in data && data.isSuccess === false) {
					captureAnalyticsEvent('query_error', {
						query_key: query.queryKey,
						error_code: 'errorCode' in data && data.errorCode,
						error_message: 'errorMessage' in data && data.errorMessage,
					});
				}
			},
			onError: (error, query) => {
				// Recovers even when the query doesn't throw to an error boundary.
				reportChunkLoadError(error);
				// Log all query errors here. If they bubble to the error boundary, we'll deduplicate there.
				// This ensures we capture errors from queries with throwOnError: false.
				if (error instanceof Error && !loggedErrors.has(error)) {
					loggedErrors.add(error);
					posthog.captureException(error, {
						extra: {
							queryKey: query.queryKey,
							source: 'react-query',
							...(isTRPCClientError<AppRouter>(error) && {
								trpcCode: error.data?.code,
								trpcPath: error.data?.path,
								// Backend correlation id, queryable in PostHog and shown to users as the support reference.
								requestId: error.data?.requestId,
							}),
							...(extractBackendHttpError(error) !== null && {
								httpStatus: extractBackendHttpError(error)!.httpStatus,
								requestUrl: extractBackendHttpError(error)!.requestUrl,
							}),
						},
					});
				}
			},
		}),
		mutationCache: new MutationCache({
			onSuccess: (data, variables, context, mutation) => {
				// Some of our API routes return an object with `isSuccess === false` if the request was not successful. We need
				// to log these to PostHog separately as they don't technically trigger exceptions.
				if (typeof data === 'object' && data !== null && 'isSuccess' in data && data.isSuccess === false) {
					captureAnalyticsEvent('mutation_error', {
						mutation_key: mutation.options.mutationKey,
						error_code: 'errorCode' in data && data.errorCode,
						error_message: 'errorMessage' in data && data.errorMessage,
					});
				}
			},
			onError: (error, variables, context, mutation) => {
				reportChunkLoadError(error);
				if (error instanceof Error && !loggedErrors.has(error)) {
					loggedErrors.add(error);
					posthog.captureException(error, {
						extra: {
							mutationKey: mutation.options.mutationKey,
							source: 'react-query',
							...(isTRPCClientError<AppRouter>(error) && {
								trpcCode: error.data?.code,
								trpcPath: error.data?.path,
								// Backend correlation id, queryable in PostHog and shown to users as the support reference.
								requestId: error.data?.requestId,
							}),
							...(extractBackendHttpError(error) !== null && {
								httpStatus: extractBackendHttpError(error)!.httpStatus,
								requestUrl: extractBackendHttpError(error)!.requestUrl,
							}),
						},
					});
				}
			},
		}),
	});

	await clearPersistentQueryCachesFromSearchParams().catch((error: unknown) => {
		console.warn('Failed to clear persisted query cache from URL search parameter.', error);
	});

	void runPersistentQueryCacheGc().catch((error: unknown) => {
		console.warn('Failed to garbage collect persisted query cache.', error);
	});

	const trpcClient = createTRPCClient<AppRouter>({
		links: createTrpcLinks(auth0Client, posthog),
	});

	const trpc = createTRPCOptionsProxy<AppRouter>({
		client: trpcClient,
		queryClient,
	});

	// Prefetch some important queries here in the bootstrapping phase so that they begin as early as possible.
	if (!isAuth0CallbackPathname(window.location.pathname)) {
		// Make a query in order to initiate Auth0 user authentication -- the significance of this query is that it
		// calls `auth0Client.getTokenSilently()`.
		void queryClient.prefetchQuery(auth0UserQueryOptions);

		// Also prefetch the current user query because the entire page suspends on it.
		void queryClient.prefetchQuery(trpc.user.getCurrent.queryOptions());
	}

	const router = createRouter({ auth0Client, queryClient, trpc, trpcClient, posthog });

	setAnalyticsRouteContextSource(() => getRouteAnalyticsContext(router));
	registerPageviewTracking(router, posthog);

	const auth0ContextValue: Auth0ContextValue = {
		client: auth0Client,
	};

	createRoot(rootElement).render(
		<StrictMode>
			<Auth0Context value={auth0ContextValue}>
				<PostHogProvider client={posthog}>
					<QueryClientProvider client={queryClient}>
						<TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
							<StyleProviders>
								<RouterProvider router={router} />
								<StaleDeployPrompt />
							</StyleProviders>
						</TRPCProvider>
					</QueryClientProvider>
				</PostHogProvider>
			</Auth0Context>
		</StrictMode>,
	);
}

void clientInit();
